/**
 * Shared enums and constants used across the SalesGod platform.
 * Single source of truth — avoids string drift between components.
 *
 * IMPORTANT: If you add values here, also update the backend copy at
 * functions/shared/enums.js (until a monorepo or shared package is set up).
 */

// ── Contact Status (Firestore field: lead_contacted) ──
export const CONTACT_STATUS = {
  NOT_CONTACTED: 'Not Contacted',
  LEAD: 'Lead',
  IN_OUTREACH: 'In Outreach',
  INTRODUCED: 'Introduced',
  FIRST_FOLLOWUP: 'First Follow-up',
  SECOND_FOLLOWUP: 'Second Follow-up',
  THIRD_FOLLOWUP: 'Third Follow-up',
  INTERESTED: 'Interested',
  NOT_INTERESTED: 'Not Interested',
  OUTREACH_REMOVED: 'Outreach: Removed',
  OUTREACH_FAILED: 'Outreach: Failed',
};

export const CONTACT_STATUS_VALUES = Object.values(CONTACT_STATUS);

// ── SafeGuard Scope ──
// SafeGuard evaluates the FIRST-TOUCH introduction email only. Its DOs / DON'Ts
// / target-audience rules are written for the intro; follow-ups continue an
// existing conversation and must never be blocked by the intro's guard.
// Returns true for any follow-up email type (follow_up_1/2/3, …).
export function isFollowUpEmailType(emailType) {
  if (!emailType || typeof emailType !== 'string') return false;
  const t = emailType.toLowerCase().trim();
  return t.startsWith('follow_up') || t.startsWith('followup') || t.startsWith('follow-up');
}

// True only for the first-contact introduction email — the one SafeGuard checks.
export function isIntroEmailType(emailType) {
  if (!emailType || typeof emailType !== 'string') return false;
  return emailType.toLowerCase().trim() === 'intro';
}

// ── Tag IDs (Firestore field: tags.{tagId}) ──
export const TAG_IDS = {
  NOT_QUALIFIED: 'not_qualified',
  NOT_INTERESTED: 'not_interested',
  COMPETITOR: 'competitor',
  UNRESPONSIVE: 'unresponsive',
  CONVERTED: 'converted',
  FAVORITE: 'favorite',
};

// Tags that visually strike through the row
export const STRIKETHROUGH_TAGS = [
  TAG_IDS.NOT_QUALIFIED,
  TAG_IDS.NOT_INTERESTED,
  TAG_IDS.COMPETITOR,
  TAG_IDS.UNRESPONSIVE,
];

// ── Flow End Reasons (Firestore field: flowEndStatus) ──
export const FLOW_END_STATUS = {
  COMPLETED: 'completed',
  CONVERTED: 'converted',
  DISQUALIFIED: 'disqualified',
  NOT_INTERESTED: 'not_interested',
  WRONG_CONTACT: 'wrong_contact',
  NURTURE: 'nurture',
};

// ── Lead Score Tiers ──
export const LEAD_SCORE_TIERS = [
  { min: 80, className: 'score-high', label: 'Hot' },
  { min: 60, className: 'score-medium', label: 'Warm' },
  { min: 40, className: 'score-low', label: 'Cool' },
  { min: 0,  className: 'score-cold', label: 'Cold' },
];

export function getLeadScoreTier(score) {
  if (score === null || score === undefined || isNaN(score)) return null;
  return LEAD_SCORE_TIERS.find(t => score >= t.min) || LEAD_SCORE_TIERS[LEAD_SCORE_TIERS.length - 1];
}

// ── Social Platforms (shared between WorldDbTableRow, SocialEditModal, DecisionMakersModal) ──
export const SOCIAL_PLATFORMS = [
  { key: 'linkedin',  icon: 'fab fa-linkedin',    label: 'LinkedIn',    placeholder: 'https://linkedin.com/in/...',   color: '#0077b5' },
  { key: 'facebook',  icon: 'fab fa-facebook',    label: 'Facebook',    placeholder: 'https://facebook.com/...',      color: '#1877f2' },
  { key: 'instagram', icon: 'fab fa-instagram',   label: 'Instagram',   placeholder: 'https://instagram.com/...',     color: '#e4405f' },
  { key: 'x',         icon: 'fab fa-x-twitter',   label: 'X / Twitter', placeholder: 'https://x.com/...',             color: '#000000' },
  { key: 'tiktok',    icon: 'fab fa-tiktok',      label: 'TikTok',      placeholder: 'https://tiktok.com/@...',       color: '#010101' },
  { key: 'youtube',   icon: 'fab fa-youtube',     label: 'YouTube',     placeholder: 'https://youtube.com/@...',      color: '#ff0000' },
  { key: 'xing',      icon: 'fab fa-xing',        label: 'Xing',        placeholder: 'https://xing.com/profile/...',  color: '#006567' },
  { key: 'website',   icon: 'fa fa-globe',        label: 'Website',     placeholder: 'https://...',                   color: '#6366f1' },
];

// ── Pacing Options ──
export const PACING_OPTIONS = {
  FORMAL: 'formal',
  NEUTRAL: 'neutral',
  CASUAL: 'casual',
};

export const PACING_LABELS = {
  [PACING_OPTIONS.FORMAL]: 'Formal',
  [PACING_OPTIONS.NEUTRAL]: 'Neutral',
  [PACING_OPTIONS.CASUAL]: 'Casual',
};

// ── Tone Tags (AI-detected writing style / vibe descriptors, 2-7 per contact) ──
export const VALID_TONE_TAGS = [
  'spiritual',
  'motivational',
  'chill',
  'corporate',
  'trendy',
  'warm',
  'funny',
  'scientific',
  'edgy',
  'luxury',
  'down_to_earth',
  'energetic',
  'bold',
  'nurturing',
  'playful',
  'elegant',
  'raw',
  'technical',
  'salesy',
  'storytelling',
  'educational',
  'emotional',
  'minimalist',
  'personal',
  'empowering',
  'sarcastic',
  'poetic',
  'rebellious',
  'wholesome',
];

// ══════════════════════════════════════════════════════════════
// Signal Intelligence Enums — SINGLE SOURCE OF TRUTH
//
// Used by: leadSignalEngine.js, analyticsTracker.js (frontend)
//          leadSignalHelper.js, analyticsHelper.js, classifyContactNano.js (backend)
//          InboxEmailAIAnalysis.js (reply subtypes)
//
// Backend mirror: functions/shared/enums.js (keep in sync)
// ══════════════════════════════════════════════════════════════

export const VALID_INDUSTRIES = [
  'SaaS', 'E-Commerce', 'Agency', 'Consulting', 'Real Estate', 'Finance',
  'Healthcare', 'Education', 'Manufacturing', 'Retail', 'Food & Beverage',
  'Fitness & Wellness', 'Legal', 'Media & Entertainment', 'Construction',
  'Automotive', 'Travel & Tourism', 'Logistics', 'Insurance', 'Non-Profit',
  'Recruiting', 'Beauty & Cosmetics', 'Photography', 'Architecture',
  'IT Services', 'Marketing', 'Design', 'Coaching', 'Events', 'Energy',
  'Cleaning & Facility', 'Trades & Crafts', 'Agriculture', 'Pet Industry',
  'Other',
];

export const VALID_OFFER_TYPES = [
  'service', 'saas', 'physical_product', 'education', 'marketplace',
  'media_content', 'financial_service', 'healthcare_service',
  'legal_service', 'recruiting', 'coaching', 'agency_service',
  'subscription', 'other',
];

export const VALID_B2B_B2C = ['b2b', 'b2c', 'both', 'unknown'];

export const VALID_SIZE_PROXY = ['SOLO', 'SMALL', 'MID', 'ENT', 'unknown'];

export const REPLY_SUBTYPES = [
  'interested', 'price_objection', 'no_need', 'later',
  'wrong_person', 'hostile', 'spam_accusation', 'neutral',
];

export const SIGNALS_VERSION = 'ls_v6';

// ── Shared DM / Recipient Helpers ──

export function parseDecisionMakers(data) {
  try {
    const raw = data?.lead_decision_makers;
    if (raw && typeof raw === 'string') return JSON.parse(raw);
    if (Array.isArray(raw)) return raw;
  } catch (_) {}
  return [];
}

/**
 * Returns the decision maker we should keep talking to for this contact.
 * On follow-ups this is the DM we already wrote to (flagged `contacted`,
 * most recent `contactedAt` if several). Falls back to the first DM in the
 * list. This keeps recipient + greeting on the same person across the whole
 * follow-up sequence instead of jumping to a different DM.
 */
export function getPrimaryDecisionMaker(data) {
  const dms = parseDecisionMakers(data);
  if (dms.length === 0) return null;
  const contacted = dms.filter(dm => dm && dm.contacted && (dm.name || dm.email));
  if (contacted.length > 0) {
    contacted.sort((a, b) => {
      const ta = Date.parse(a.contactedAt || '') || 0;
      const tb = Date.parse(b.contactedAt || '') || 0;
      return tb - ta;
    });
    return contacted[0];
  }
  return dms.find(dm => dm && dm.name) || dms[0];
}

export function getFirstDmName(data) {
  const dm = getPrimaryDecisionMaker(data);
  if (dm && dm.name) return dm.name.split(' ')[0];
  return data?.lead_firstname || '';
}

/**
 * Looks up the emailPreference from the active template in userTopics.
 * Returns 'general' | 'decision_maker' or null if 'auto' / not found.
 */
function findActiveTopic(selectedTopic, userTopics, topicTypeMapping) {
  if (!selectedTopic || !userTopics) return null;
  const topicType = topicTypeMapping?.[selectedTopic];
  for (const topicData of Object.values(userTopics)) {
    if (!topicData || typeof topicData !== "object") continue;
    if (topicData.topic === selectedTopic || topicData.type === selectedTopic || topicData.type === topicType) {
      return topicData;
    }
  }
  return null;
}

/** 'strict' skips a contact with only a company inbox. Anything else uses it. */
export function normalizeDmFallback(value) {
  return value === "strict" ? "strict" : "info";
}

export function getActiveTemplatePref(selectedTopic, userTopics, topicTypeMapping) {
  const topicData = findActiveTopic(selectedTopic, userTopics, topicTypeMapping);
  if (!topicData) return null;
  if (topicData.emailPreference && topicData.emailPreference !== "auto") {
    return topicData.emailPreference;
  }
  return null;
}

export function getActiveDmFallback(selectedTopic, userTopics, topicTypeMapping) {
  return normalizeDmFallback(findActiveTopic(selectedTopic, userTopics, topicTypeMapping)?.dmFallback);
}

// ── Privacy / Legal / Compliance Mailbox Filter ──
// privacy@, datenschutz@, dsgvo@, legal@, abuse@, compliance@, … are function
// mailboxes for data-protection / legal matters, never an outreach contact. We
// flag and skip them so they are never written onto a lead.
// Mirror in functions/helpers/contactEmail.js — keep in sync.
const PRIVACY_EMAIL_KEYWORDS = [
  'privacy', 'datenschutz', 'dataprotection', 'gdpr', 'dsgvo',
  'datenschutzbeauftragter', 'privacyofficer',
  'legal', 'abuse', 'compliance', 'rechtsabteilung',
];
const PRIVACY_EMAIL_EXACT = new Set(['dpo', 'dsb', 'recht']);

/** True when an address is a privacy / legal / compliance mailbox (skip it). */
export function isPrivacyEmail(email) {
  if (!email || typeof email !== 'string' || !email.includes('@')) return false;
  const local = email.toLowerCase().trim().split('@')[0].split('+')[0];
  const norm = local.replace(/[._-]/g, '');
  if (!norm) return false;
  if (PRIVACY_EMAIL_EXACT.has(local) || PRIVACY_EMAIL_EXACT.has(norm)) return true;
  return PRIVACY_EMAIL_KEYWORDS.some((k) => norm.includes(k));
}

// ── Contact Email Extraction (general + decision maker emails) ──
export function getAllContactEmails(rowData) {
  const emails = [];
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const seen = new Set();

  if (rowData.lead_email) {
    rowData.lead_email.split(/[,;\s]+/).forEach(raw => {
      const e = raw.trim();
      if (emailRegex.test(e) && !seen.has(e.toLowerCase())) {
        seen.add(e.toLowerCase());
        emails.push({ email: e, label: e, source: 'general' });
      }
    });
  }

  let dms = [];
  try {
    const raw = rowData.lead_decision_makers;
    if (raw && typeof raw === 'string') dms = JSON.parse(raw);
    else if (Array.isArray(raw)) dms = raw;
  } catch (_) {}

  dms.forEach(dm => {
    const e = (dm.email || '').trim();
    const hasValidEmail = emailRegex.test(e);
    const namePart = dm.name || '';
    const rolePart = dm.role ? ` (${dm.role})` : '';

    if (!hasValidEmail) {
      if (dm.emailValidationFailed === true && namePart) {
        emails.push({
          email: '',
          label: `${namePart}${rolePart}`,
          source: 'decision_maker',
          dmName: namePart,
          dmRole: dm.role || '',
          emailChecked: true,
          emailVerified: false,
          emailValidationStatus: 'invalid',
          emailMissing: true,
        });
      }
      return;
    }

    const dmEntry = {
      email: e,
      label: namePart ? `${namePart}${rolePart} <${e}>` : e,
      source: 'decision_maker',
      dmName: dm.name || '',
      dmRole: dm.role || '',
      emailChecked: dm.emailChecked === true,
      emailVerified: dm.emailVerified === true,
      emailValidationStatus: dm.emailValidationStatus || null,
      emailMissing: false,
      contacted: dm.contacted === true,
      contactedAt: dm.contactedAt || null,
    };

    const existingIdx = emails.findIndex(x => x.email.toLowerCase() === e.toLowerCase());
    if (existingIdx !== -1) {
      if (namePart) emails[existingIdx] = dmEntry;
    } else {
      seen.add(e.toLowerCase());
      emails.push(dmEntry);
    }
  });

  emails.sort((a, b) => {
    const aDm = a.source === 'decision_maker';
    const bDm = b.source === 'decision_maker';
    if (aDm && !bDm) return -1;
    if (!aDm && bDm) return 1;
    // Within decision makers, keep the one we already contacted first so it
    // becomes the default recipient on follow-ups.
    if (aDm && bDm) {
      if (a.contacted && !b.contacted) return -1;
      if (!a.contacted && b.contacted) return 1;
    }
    return 0;
  });

  return emails;
}

/**
 * True when a contact can be written to — i.e. it has at least one sendable
 * email address, either the primary lead_email OR a decision-maker email.
 * Used to keep the denormalized `has_email` index in sync with DM emails and
 * as the single eligibility check across send modes / outreach / flows.
 */
export function computeHasEmail(rowData) {
  return getAllContactEmails(rowData).some(e => e.email && e.email.trim() !== '' && !e.emailMissing);
}

export function getDecisionMakersWithoutEmail(rowData) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  let dms = [];
  try {
    const raw = rowData.lead_decision_makers;
    if (raw && typeof raw === 'string') dms = JSON.parse(raw);
    else if (Array.isArray(raw)) dms = raw;
  } catch (_) {}

  return dms
    .filter(dm => !emailRegex.test((dm.email || '').trim()) && (dm.name || '').trim())
    .map(dm => ({ name: dm.name.trim(), role: (dm.role || '').trim() }));
}

/**
 * Resolves the preferred email address for a contact based on template emailPreference.
 * Priority: emailPreference > lead_last_email > first available.
 * Returns the matching email entry from getAllContactEmails, or null.
 */
export function resolvePreferredEmail(contact, emailPreference, options = {}) {
  const emails = getAllContactEmails(contact);
  if (emails.length === 0) return null;
  const dmFallback = normalizeDmFallback(options && options.dmFallback);

  // Highest priority for follow-ups: the exact address we used on the last
  // email, so the thread stays with the same person.
  const lastRecipient = contact.lead_last_email?.recipientEmail;
  if (lastRecipient) {
    const lastMatch = emails.find(e => e.email && e.email.toLowerCase() === lastRecipient.toLowerCase());
    if (lastMatch) return lastMatch;
  }

  if (emailPreference === 'general') {
    return emails.find(e => e.source === 'general') || emails[0];
  }

  // For DM / auto preferences, re-select the decision maker we already
  // contacted (durable `contacted` flag survives backend sends) before
  // falling back to the first DM in the list.
  const contactedDm = emails.find(e => e.source === 'decision_maker' && e.contacted && e.email);
  if (contactedDm) return contactedDm;

  if (emailPreference === 'decision_maker') {
    const dm = emails.find(e => e.source === 'decision_maker' && e.email);
    if (dm) return dm;
    if (dmFallback === 'strict') return null;
    return emails.find(e => e.source === 'general' && e.email) || null;
  }

  return emails[0];
}

/**
 * Resolves recipient name/role for a contact based on the selected or last-used email.
 * If the target email belongs to a decision maker, returns that DM's name/role.
 * If the target email is a general/company email, returns empty name/role so the
 * greeting addresses the team/company instead of a specific person.
 */
export function getRecipientInfoForContact(contact, selectedEmailAddress) {
  const emails = getAllContactEmails(contact);
  const targetEmail = selectedEmailAddress
    || contact.lead_last_email?.recipientEmail
    || (emails[0] && emails[0].email);

  if (targetEmail) {
    const match = emails.find(e => e.email && e.email.toLowerCase() === targetEmail.toLowerCase());
    if (match && match.source === 'decision_maker') {
      return { name: match.dmName || '', role: match.dmRole || '' };
    }
  }

  // Target email is general/company OR no match — address the decision maker
  // we already contacted (or the first one) by name so the greeting matches
  // the recipient picked by resolvePreferredEmail. Even when sending to info@,
  // we address the DM by name (formal greeting).
  const primaryDm = getPrimaryDecisionMaker(contact);
  if (primaryDm && primaryDm.name) {
    return { name: primaryDm.name || '', role: primaryDm.role || '' };
  }

  return { name: getFirstDmName(contact), role: '' };
}

// ── Valid Tag Vocabulary (used by rule engine + LLM constraint) ──
// Tags describe WHAT a contact does / uses — orthogonal to industry & offerType.
// Keep alphabetically sorted for diffing; add sparingly.
// ── Consumer Email Providers (B2C) ──
// Used by BlackList.js and SmartInboxEmail.js to distinguish custom domains from consumer providers.
export const CONSUMER_EMAIL_PROVIDERS = [
  '@gmail.com', '@googlemail.com',
  '@gmx.de', '@gmx.at', '@gmx.ch', '@gmx.net', '@gmx.com',
  '@web.de',
  '@yahoo.com', '@yahoo.de', '@yahoo.at', '@yahoo.ch',
  '@outlook.com', '@outlook.de', '@outlook.co.uk', '@hotmail.com', '@hotmail.de', '@hotmail.co.uk',
  '@live.com', '@live.de', '@msn.com',
  '@t-online.de',
  '@freenet.de',
  '@aol.com', '@aol.de',
  '@icloud.com', '@me.com', '@mac.com',
  '@protonmail.com', '@proton.me',
  '@mail.com', '@email.com',
  '@arcor.de', '@vodafone.de', '@1und1.de', '@online.de',
  '@posteo.de', '@mailbox.org',
  '@tutanota.com', '@zoho.com', '@yandex.com', '@mail.ru',
  '@a1.net', '@chello.at', '@aon.at', '@drei.at',
  '@bluewin.ch', '@sunrise.ch', '@swissonline.ch',
];

export const VALID_TAGS = [
  'ai', 'amazon', 'automation', 'ayurveda', 'b2b_sales', 'branding',
  'coaching', 'content_creation', 'crm', 'data_analytics',
  'ecommerce', 'email_marketing', 'family', 'google_ads', 'healing',
  'hr_recruiting', 'influencer', 'lead_generation',
  'local_business', 'meditation', 'meta_ads', 'mobile_app', 'music',
  'personal_branding', 'photography', 'premium', 'real_estate', 'seo',
  'shopify', 'social_media', 'spirituality', 'startup',
  'sustainability', 'therapy', 'video', 'web_design', 'wordpress', 'yoga',
];
